import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  confirmSignIn,
  getCurrentUser,
  signIn,
  SignInInput,
  signOut,
  signUp,
} from 'aws-amplify/auth';
import { EMPTY, from, Observable, of, throwError } from 'rxjs';
import {
  catchError,
  finalize,
  map,
  switchMap,
  tap,
  timeout,
} from 'rxjs/operators';
import {
  AUTH_FLOW_TYPES,
  AUTH_ROUTES,
  COGNITO_ERRORS,
  DEV_SANDBOX_DOMAIN,
  SIGN_IN_STEPS,
} from '../constants/auth-constants';
import { classifyAuthError } from '../error-handling/auth-error-handler';
import { AuthSession, SignInResult } from '../models/auth.model';
import { AuthStore } from '../stores/auth-store';
import { generateSecurePassword } from '../utils/auth-utils';
import { AuthTokenService } from './auth-token';
import { UserAuthDataService } from 'src/app/@data-services/user-auth/user-auth.data-service';
import { MixpanelService } from 'src/app/@services/mixpanel.service';
import { ExternalRegistrationStateService } from 'src/app/@stores/external-registration-state.service';
import {
  ENROLL_EVENT_REPORT,
  ENROLLED_USER_SESSION,
} from 'src/app/@constants/local-storage-key-constants';
import { removeLocalStorageItem } from 'src/app/@utils/local-storage-util';
import { ReportsEventRouteSlugService } from 'src/app/@services/reports-event-route-slug.service';

@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  private readonly _router = inject(Router);
  private readonly _authStore = inject(AuthStore);
  private readonly _tokenService = inject(AuthTokenService);
  private readonly _userAuthDataService = inject(UserAuthDataService);
  private readonly _mixpanelService = inject(MixpanelService);
  private readonly _externalRegistrationStateService = inject(
    ExternalRegistrationStateService
  );
  private readonly _slugService = inject(ReportsEventRouteSlugService);

  private _currentAuthEmail: string | null = null;
  private _currentEventIdentifier: string | null = null;

  authenticate$(
    email: string,
    eventIdentifier?: string
  ): Observable<SignInResult> {
    const normalizedEmail = email.toLowerCase().trim();
    this._currentAuthEmail = normalizedEmail;
    this._currentEventIdentifier = eventIdentifier ?? null;

    return this._performSignIn$(normalizedEmail).pipe(
      catchError((error) => {
        if (
          this._isErrorOfType(error, COGNITO_ERRORS.USER_NOT_FOUND) ||
          (error &&
            typeof error === 'object' &&
            'type' in error &&
            (error as { type: string }).type === 'USER_NOT_FOUND')
        ) {
          return this._signUpAndSignIn$(normalizedEmail, eventIdentifier);
        }
        return throwError(() => error);
      })
    );
  }

  private _signUpAndSignIn$(
    email: string,
    eventIdentifier?: string
  ): Observable<SignInResult> {
    const resolvedDomain = this._resolveEventDomain();

    return from(
      signUp({
        username: email,
        password: generateSecurePassword(),
        options: {
          userAttributes: {
            email,
            'custom:domain': resolvedDomain,
            ...(eventIdentifier && { 'custom:lastEvent': eventIdentifier }),
          },
          clientMetadata: {
            domain: resolvedDomain,
            ...(eventIdentifier && { eventIdentifier }),
          },
        },
      })
    ).pipe(
      switchMap(() => from(signOut({ global: false }))),
      switchMap(() => this._performSignIn$(email)),
      catchError((error) => {
        if (this._isErrorOfType(error, COGNITO_ERRORS.USERNAME_EXISTS)) {
          return from(signOut({ global: false })).pipe(
            switchMap(() => this._performSignIn$(email))
          );
        }
        return throwError(() => classifyAuthError(error));
      })
    );
  }

  verifyOTP$(otp: string): Observable<boolean> {
    const resolvedDomain = this._resolveEventDomain();
    return from(
      confirmSignIn({
        challengeResponse: otp,
        options: {
          clientMetadata: {
            username: this._currentAuthEmail ?? '',
            domain: resolvedDomain,
            ...(this._currentEventIdentifier && {
              eventIdentifier: this._currentEventIdentifier,
            }),
          },
        },
      })
    ).pipe(
      tap(() => {
        this._authStore.resetState();
      }),
      map((result) => result.isSignedIn),
      catchError((error) => throwError(() => classifyAuthError(error)))
    );
  }

  resendOTP$(
    email: string,
    eventIdentifier?: string
  ): Observable<SignInResult> {
    const normalizedEmail = email.toLowerCase().trim();
    this._currentAuthEmail = normalizedEmail;
    if (eventIdentifier) {
      this._currentEventIdentifier = eventIdentifier;
    }
    return this._performSignIn$(normalizedEmail);
  }

  logout$(redirectToLogin: boolean = true): Observable<void> {
    if (this._authStore.$isLoggingOut()) {
      return EMPTY;
    }

    this._authStore.setIsLoggingOut(true);

    return from(signOut({ global: true })).pipe(
      tap(() => {
        this._clearAllState();
        this._authStore.emitLogout();
      }),
      tap(() => {
        if (redirectToLogin) {
          this._router.navigate([AUTH_ROUTES.LOGIN]);
        }
      }),
      catchError((error) => {
        this._clearAllState();
        if (redirectToLogin) {
          this._router.navigate([AUTH_ROUTES.LOGIN]);
        }
        return throwError(() => classifyAuthError(error));
      }),
      finalize(() => {
        this._authStore.setIsLoggingOut(false);
      })
    );
  }

  checkSession$(): Observable<AuthSession> {
    return from(getCurrentUser()).pipe(
      switchMap(() => this._tokenService.getValidToken$()),
      map(() => {
        const session = this._authStore.getSessionOrNull();
        if (!session) {
          return {
            tokens: null,
            isAuthenticated: false,
            lastFetched: Date.now(),
          };
        }
        return session;
      }),
      catchError(() =>
        of({
          tokens: null,
          isAuthenticated: false,
          lastFetched: Date.now(),
        })
      )
    );
  }

  getUserEmail$(): Observable<string | null> {
    return from(getCurrentUser()).pipe(
      map((user) => user.signInDetails?.loginId ?? user.username ?? null),
      catchError(() => of(null))
    );
  }

  getCurrentAuthEmail(): string | null {
    return this._currentAuthEmail;
  }

  private _performSignIn$(email: string): Observable<SignInResult> {
    const resolvedDomain = this._resolveEventDomain();
    const signInInput: SignInInput = {
      username: email,
      options: {
        authFlowType: AUTH_FLOW_TYPES.CUSTOM_WITHOUT_SRP,
        clientMetadata: {
          username: email,
          domain: resolvedDomain,
          ...(this._currentEventIdentifier && {
            eventIdentifier: this._currentEventIdentifier,
          }),
        },
      },
    };

    // SYN-1957: Amplify v6 signIn() does not reliably forward clientMetadata
    // to Cognito's CreateAuthChallenge trigger, so the OTP email ends up using
    // whatever event was set on custom:lastEvent previously. Persist the
    // current event identifier on the Cognito user before initiating signIn()
    // so the trigger resolves the correct per-event OTP branding.
    // Failures are non-fatal: we still proceed to signIn() so the user is not
    // blocked by a transient backend error.
    // 3s is well above the p99 latency for AdminUpdateUserAttributes; if the
    // call stalls past that, abandon it so the user is not blocked from
    // signing in (catchError below converts the timeout into a no-op).
    const contextUpdate$ = this._currentEventIdentifier
      ? this._userAuthDataService
          .setLoginEventContext(email, this._currentEventIdentifier)
          .pipe(
            timeout(3000),
            catchError(() => of({ success: false }))
          )
      : of({ success: true });

    return contextUpdate$.pipe(
      switchMap(() => from(signIn(signInInput))),
      map((result) => {
        if (result.isSignedIn) {
          return {
            success: true,
            message: 'Already signed in',
            nextStep: 'DONE' as const,
          };
        }

        if (
          result.nextStep?.signInStep ===
          SIGN_IN_STEPS.CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE
        ) {
          return {
            success: true,
            message: 'OTP sent to your email',
            nextStep: 'CONFIRM_OTP' as const,
          };
        }

        return {
          success: false,
          message: 'Unexpected authentication state',
        };
      }),
      catchError((error) => throwError(() => classifyAuthError(error)))
    );
  }

  private _clearAllState(): void {
    this._authStore.resetState();
    this._tokenService.clearPendingRefresh();
    this._mixpanelService.reset();
    this._externalRegistrationStateService.reset();
    removeLocalStorageItem(ENROLL_EVENT_REPORT);
    removeLocalStorageItem(ENROLLED_USER_SESSION);
    this._currentAuthEmail = null;
  }

  private _resolveEventDomain(): string {
    const slug =
      this._currentEventIdentifier ||
      this._slugService.effectiveSlugWithPersistence();
    if (slug) {
      return `${slug.toLowerCase()}.synopsis.rozie.ai`;
    }
    if (
      typeof window !== 'undefined' &&
      window.location.hostname === 'localhost'
    ) {
      return DEV_SANDBOX_DOMAIN;
    }
    return typeof window !== 'undefined'
      ? window.location.hostname
      : DEV_SANDBOX_DOMAIN;
  }

  private _isErrorOfType(error: unknown, errorName: string): boolean {
    if (error && typeof error === 'object') {
      const err = error as { name?: string; code?: string };
      return err.name === errorName || err.code === errorName;
    }
    return false;
  }
}
